import { Typography } from '@mui/material';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';

import ContentLoader from '@components/ContentLoader';
import Icon from '@components/Icon/Icon';
import StackedIcon from '@components/Icon/StackedIcon';
import UnitPill from '@components/UnitPill';

import { VITAL_SIGN_TYPES } from '@constants/fhir';

import { useConversion } from '@hooks/useConversion';

import type { LabResultObservationRecord } from '@models/lab-result';
import type { ObservationRecord } from '@models/observation';

import { convertUnit, getUnit, getUnits } from '@utils/conversion';
import { getDeviceBrandName } from '@utils/device-brand';
import { formatVitalSign } from '@utils/format';
import { getHeightMetres } from '@utils/height';

import { FontWeight } from '../../theme/typography';
import {
  Body,
  CardContainer,
  CardRoot,
  ChevronCircle,
  IconWrap,
  SourceLine,
  TextBlock,
  ValueRow,
} from './VitalSignCard.style';

const messages = defineMessages({
  noRecords: {
    id: 'vitalSignCard.noRecords',
    defaultMessage: 'No Known Metrics',
  },
  sleep: {
    id: 'vitalSignCard.sleep',
    defaultMessage: 'Sleep',
  },
  fitness: {
    id: 'vitalSignCard.fitness',
    defaultMessage: 'Daily',
  },
  avgHr: {
    id: 'vitalSignCard.avgHeartRate',
    defaultMessage: '{type} Avg Heart Rate',
  },
});

export default function VitalSignCard({
  onClick,
  code,
  loading = false,
  record,
}: {
  onClick?: () => void;
  code: string;
  loading?: boolean;
  record?: LabResultObservationRecord | ObservationRecord;
}) {
  const { formatMessage } = useIntl();
  const {
    state: { conversion },
  } = useConversion();

  const {
    precision = 2,
    format,
    icon,
    messageId,
    unit,
  } = VITAL_SIGN_TYPES[code];
  const units = getUnits(code);
  const canConvert = Boolean(units.SI && units.CON);

  const convertedUnit = getUnit(unit, conversion[code] ? 'CON' : 'SI', code);

  const displayRecordValue = () => {
    if (!record) {
      return '- -';
    }
    if (code === 'blood-type') {
      return record.value ?? '- -';
    }
    if (code === 'blood-pressure') {
      return convertUnit(
        record.value,
        record.unit,
        conversion[code] ? 'CON' : 'SI',
        precision,
        code
      );
    }
    if (code === 'body-height') {
      // Normalise metres / feet+inches to metres, then convert once.
      const metres = getHeightMetres(record);
      if (metres === null) {
        return '- -';
      }
      return formatVitalSign(
        [
          Number(
            convertUnit(
              metres,
              'm',
              conversion[code] ? 'CON' : 'SI',
              precision,
              code
            )
          ),
        ],
        false,
        format
          ? // eslint-disable-next-line @typescript-eslint/no-unused-vars
            (value, _unit) => format(value, convertedUnit)
          : undefined
      );
    }

    return formatVitalSign(
      [
        Number(
          convertUnit(
            record.value,
            record.unit,
            conversion[code] ? 'CON' : 'SI',
            precision,
            code
          )
        ),
      ],
      false,
      format
        ? // eslint-disable-next-line @typescript-eslint/no-unused-vars
          (value, _unit) => format(value, convertedUnit)
        : undefined
    );
  };

  const getText = () => {
    if (record?.code === 'average-heart-rate') {
      return formatMessage(messages.avgHr, {
        type: record.category?.includes('fitness')
          ? formatMessage(messages.fitness)
          : record.category?.includes('sleep')
            ? formatMessage(messages.sleep)
            : '',
      });
    }
    return formatMessage({ id: messageId });
  };

  /** Reading date, or the empty state when there is nothing to date. */
  const dateText = record?.id
    ? record.getObservationDateStr()
    : formatMessage(messages.noRecords);

  /**
   * Brand that supplied the reading.
   *
   * Sits beside the date rather than on a line of its own: it is attribution
   * for the value above it either way, and a second line pushed the card
   * taller than the grid row it shares with cards that have no source.
   */
  const brand = record?.id ? getDeviceBrandName(record.source) : undefined;

  const isStackedIcon =
    code === 'body-chest-cm' ||
    code === 'body-waist-cm' ||
    code === 'body-hips-cm' ||
    code === 'body-thigh-cm';

  return (
    <CardContainer>
      <CardRoot onClick={onClick}>
        <ContentLoader loading={loading}>
          <IconWrap>
            <StackedIcon
              icons={icon.map((i) => ({
                ...i,
                color:
                  i.name === 'hyphen' && isStackedIcon
                    ? 'info.light'
                    : 'primary.main',
                size: i.name === 'hyphen' && isStackedIcon ? 'xs' : i.size,
                transform:
                  i.name === 'hyphen' && isStackedIcon
                    ? code === 'body-chest-cm'
                      ? 'shrink-2 up-3'
                      : code === 'body-waist-cm'
                        ? 'shrink-4 down-1'
                        : code === 'body-hips-cm'
                          ? 'shrink-2 down-3'
                          : 'shrink-2 down-6'
                    : i.transform,
              }))}
            />
          </IconWrap>

          <Body>
            <ValueRow>
              <Typography
                fontSize="inherit"
                lineHeight={1}
                noWrap
                variant="h4"
                sx={{ fontWeight: FontWeight.ExtraLight }}
              >
                {displayRecordValue()}
              </Typography>
              {canConvert ? (
                <UnitPill code={code} disabled={loading} />
              ) : (
                <Typography
                  color="text.secondary"
                  lineHeight={1}
                  variant="subtitle1"
                  sx={{ fontWeight: FontWeight.ExtraLight }}
                >
                  {convertedUnit}
                </Typography>
              )}
            </ValueRow>

            <TextBlock>
              <Typography lineHeight={1.25} variant="subtitle1">
                {getText().trim()}
              </Typography>
              <SourceLine>
                <Typography
                  color="text.secondary"
                  flex="0 0 auto"
                  noWrap
                  variant="subtitle2"
                >
                  {dateText}
                </Typography>
                {brand ? (
                  <>
                    <Typography
                      color="grey.400"
                      flex="0 0 auto"
                      variant="subtitle2"
                    >
                      &middot;
                    </Typography>
                    <Typography
                      color="text.secondary"
                      flex="1 1 auto"
                      minWidth={0}
                      noWrap
                      title={brand}
                      variant="subtitle2"
                    >
                      {brand}
                    </Typography>
                  </>
                ) : null}
              </SourceLine>
            </TextBlock>
          </Body>

          {code !== 'blood-type' && onClick ? (
            <ChevronCircle>
              <Icon color="primary.main" name="chevron-right" size="sm" />
            </ChevronCircle>
          ) : null}
        </ContentLoader>
      </CardRoot>
    </CardContainer>
  );
}
